import React, { useEffect, useState } from "react";
import { Plus, Trash2, Droplet, X, Search, Sparkles, Barcode } from "lucide-react";
import { toast } from "sonner";
import { api, palette } from "@/lib/api";
import { Card } from "@/pages/bitepal/Home";

const MEALS = ["Breakfast", "Lunch", "Dinner", "Snack"];
const gradeColor = { A: palette.emerald, B: "#84CC16", C: palette.yellow, D: palette.orange, F: palette.red };

export default function Diary() {
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null); // meal
  const load = async () => { try { setD(await api("/food/diary")); } catch {} };
  useEffect(() => { load(); }, []);

  const remove = async (id) => { try { await api(`/food/entry/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  const addWater = async () => { try { await api("/food/water", { method: "POST", body: { ml: 250 } }); load(); } catch {} };

  if (!d) return <div className="text-slate-500">Loading…</div>;
  const t = d.totals;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-3xl font-extrabold">Food Diary</h1>
        <button data-testid="diary-add-button" onClick={() => setModal("Breakfast")} className="w-10 h-10 rounded-full bg-[#651F33] text-[#F5F0E8] flex items-center justify-center"><Plus size={22} /></button>
      </div>

      <Card>
        <p className="text-3xl font-extrabold">{Math.round(t.calories)} <span className="text-base text-slate-500 font-semibold">kcal today</span></p>
        <div className="flex mt-4">
          {[["Protein", t.protein_g, palette.emerald], ["Carbs", t.net_carbs_g || t.total_carbs_g, palette.blue], ["Fat", t.fat_g, "#F59E0B"], ["Fiber", t.fiber_g, palette.violet]].map(([l, v, c]) =>
            <div key={l} className="flex-1 text-center"><p className="text-base font-bold" style={{ color: c }}>{Math.round(v)}g</p><p className="text-[11px] text-slate-500">{l}</p></div>)}
        </div>
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#E3D9C8]">
          <Droplet size={16} className="text-[#22D3EE]" /><span className="text-sm text-slate-500">{d.water_ml} ml water</span>
          <button data-testid="diary-water-add" onClick={addWater} className="ml-auto text-[#22D3EE] font-bold text-sm">+250 ml</button>
        </div>
      </Card>

      {MEALS.map((meal) => {
        const items = d.meals[meal] || [];
        const cals = items.reduce((s, i) => s + (i.calories || 0), 0);
        return (
          <div key={meal} className="mt-5">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">{meal}</span>
              <div className="flex items-center gap-3"><span className="text-sm text-slate-500">{Math.round(cals)} kcal</span>
                <button onClick={() => setModal(meal)} className="text-[#651F33]"><Plus size={20} /></button></div>
            </div>
            {items.length === 0 ? <p className="text-sm text-slate-400 italic">Nothing logged yet</p> :
              items.map((it) => (
                <div key={it.entry_id} className="flex items-center gap-3 bg-white border border-[#E3D9C8] rounded-2xl p-3 mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center font-extrabold" style={{ background: (gradeColor[it.bitepal_score] || "#64748B") + "22", color: gradeColor[it.bitepal_score] || "#64748B" }}>{it.bitepal_score}</div>
                  <div className="flex-1 min-w-0"><p className="font-semibold truncate">{it.name}</p><p className="text-xs text-slate-500">{Math.round(it.calories)} kcal · P{Math.round(it.protein_g)} C{Math.round(it.total_carbs_g)} F{Math.round(it.fat_g)}</p></div>
                  <button onClick={() => remove(it.entry_id)} className="text-slate-400 hover:text-[#F43F5E]"><Trash2 size={18} /></button>
                </div>
              ))}
          </div>
        );
      })}

      {modal && <LogModal meal={modal} onClose={() => setModal(null)} onAdded={load} />}
    </div>
  );
}

function LogModal({ meal, onClose, onAdded }) {
  const [mode, setMode] = useState("search");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState({});

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true); setItems([]);
    try {
      if (mode === "search") setItems((await api(`/food/search?q=${encodeURIComponent(q)}`)).results);
      else if (mode === "text") setItems((await api("/food/analyze-text", { method: "POST", body: { text: q } })).items || []);
      else { setItems([await api(`/food/barcode/${q.trim()}`)]); }
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const add = async (it, idx) => {
    try {
      await api("/food/entry", { method: "POST", body: {
        name: it.name, brand_name: it.brand_name, meal_category: meal, calories: it.calories || 0,
        protein_g: it.protein_g || 0, total_carbs_g: it.total_carbs_g || 0, fat_g: it.fat_g || 0,
        fiber_g: it.fiber_g || 0, sugar_g: it.sugar_g || 0, sodium_mg: it.sodium_mg || 0,
        bitepal_score: it.bitepal_score, key_highlights: it.key_highlights || [],
      }});
      setAdded((a) => ({ ...a, [idx]: true })); onAdded();
    } catch (e) { toast.error(e.message); }
  };

  const MODES = [["search", "Search", Search], ["text", "Describe", Sparkles], ["barcode", "Barcode", Barcode]];
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#ECE4D6] border border-[#E3D9C8] rounded-3xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-extrabold">Log to {meal}</h2><button onClick={onClose}><X className="text-slate-500" /></button></div>
        <div className="flex gap-2 mb-3">
          {MODES.map(([k, l, Icon]) => <button key={k} onClick={() => { setMode(k); setItems([]); }} className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-semibold ${mode === k ? "border-[#651F33] bg-[#651F33]/10 text-[#651F33]" : "border-[#E3D9C8] text-slate-500"}`}><Icon size={16} /> {l}</button>)}
        </div>
        <div className="flex gap-2">
          <input data-testid={mode === "text" ? "diary-text-log-input" : "diary-search-input"} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder={mode === "search" ? "Search foods…" : mode === "text" ? "e.g. two eggs and toast" : "Barcode number"}
            className="flex-1 bg-white border border-[#E3D9C8] rounded-2xl px-4 py-3 text-slate-900 outline-none focus:border-[#651F33]" />
          <button data-testid={mode === "text" ? "diary-text-log-submit" : "diary-search-submit"} onClick={run} className="w-12 rounded-2xl bg-[#651F33] text-[#F5F0E8] flex items-center justify-center"><Search size={20} /></button>
        </div>
        {loading && <p className="text-slate-500 mt-4 text-center">Analyzing…</p>}
        <div className="mt-4 space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-white border border-[#E3D9C8] rounded-2xl p-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center font-extrabold" style={{ background: (gradeColor[it.bitepal_score] || "#64748B") + "22", color: gradeColor[it.bitepal_score] || "#64748B" }}>{it.bitepal_score || "?"}</div>
              <div className="flex-1 min-w-0"><p className="font-semibold truncate">{it.name}</p><p className="text-xs text-slate-500">{Math.round(it.calories || 0)} kcal · P{Math.round(it.protein_g || 0)} C{Math.round(it.total_carbs_g || 0)} F{Math.round(it.fat_g || 0)}</p></div>
              <button onClick={() => add(it, idx)} disabled={added[idx]} className={`w-10 h-10 rounded-full flex items-center justify-center ${added[idx] ? "bg-[#651F33]/20 text-[#651F33]" : "bg-[#651F33] text-[#F5F0E8]"}`}><Plus size={20} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
